import React from 'react'
import { Portal } from 'react-portal'
import { useInView } from 'react-intersection-observer'

// Utils
import useUserProfile from 'modules/user/useUserProfile'
import { useIsNodeContentTruncated } from 'utils/hooks/useIsNodeContentTruncated'
import { capitalize } from 'utils'

// Icons
import { ReactComponent as NoCommentSvg } from 'icons/no-comment.svg'

// Components
import Spinner from 'components/Spinner'
import { DropdownTitle, AccessLevelSelect } from '../AccessLevelSelect'
import { Flex } from 'components/Box'
import Breakpoint from 'components/Breakpoint'
import ConfirmationDialog from 'components/ConfirmationDialog'
import { Truncate } from 'components/Truncate'

// Styles
import {
  Wrapper,
  Avatar,
  Pill,
  TooltipWithTruncatedContent,
  UserEmail,
  GuestBadge,
  NoCommentIcon,
} from './ShareMember.styles'

// Hooks
import { useDeleteMember } from './useDeleteMember'
import { useUpdateMember } from './useUpdateMember'

// Types
import { ShareForSettingsModal } from '../types'
import { ShareMembershipInfoFragment } from 'gqlTypes'
import { AdminPillVariant } from 'components/Pill'

type InviteStatus = Pick<
  ShareMembershipInfoFragment,
  'inviteStatus' | 'workspaceMembership'
>

export type ShareForShareMember = Pick<
  ShareForSettingsModal,
  | 'identifier'
  | 'allowedPrivateAccessLevels'
  | 'userCanUpdateCommentsEnabled'
  | 'libraryEnabled'
  | 'workspaceAccessLevel'
  | 'workspaceCommentsEnabled'
  | 'isDraft'
>

interface ShareMemberProps {
  share: ShareForShareMember
  member: ShareMembershipInfoFragment
}

const badgeForMemberRole = ({
  workspaceMembership,
  inviteStatus,
}: InviteStatus) => {
  switch (inviteStatus) {
    case 'INVITED':
      return (
        <Pill role="status-badge" color="gray" variant={{ kind: 'secondary' }}>
          Invite pending
        </Pill>
      )
    case 'REQUESTED':
      return (
        <Pill role="status-badge" color="gray" variant={{ kind: 'secondary' }}>
          Requested
        </Pill>
      )
    case 'ACCEPTED':
      switch (workspaceMembership?.role) {
        case 'ADMIN':
          return (
            <Pill role="status-badge" color="indigo" variant={AdminPillVariant}>
              Admin
            </Pill>
          )
        case 'GUEST':
          return <GuestBadge role="status-badge">Guest</GuestBadge>
        case 'MEMBER':
          return null
      }
      return <GuestBadge role="status-badge">Guest</GuestBadge>
    default:
      return null
  }
}

const NonUserInfo = ({ member }: { member: ShareMembershipInfoFragment }) => {
  const [
    isEmailTruncated,
    emailRef,
  ] = useIsNodeContentTruncated<HTMLDivElement>()

  return (
    <TooltipWithTruncatedContent
      placement="top"
      spacing="10px"
      content={member.email}
      disabled={!isEmailTruncated}>
      <Truncate ref={emailRef}>{member.email}</Truncate>
      {!member.commentsEnabled && (
        <Breakpoint on="xs">
          <NoCommentIcon
            as={NoCommentSvg}
            aria-label="Member cannot comment on Artboards."
          />
        </Breakpoint>
      )}
    </TooltipWithTruncatedContent>
  )
}

const UserInfo = ({ member }: { member: ShareMembershipInfoFragment }) => {
  const [
    isEmailTruncated,
    emailRef,
  ] = useIsNodeContentTruncated<HTMLDivElement>()

  const [isNameTruncated, nameRef] = useIsNodeContentTruncated<HTMLDivElement>()
  const { data: userData } = useUserProfile(true)

  const user = member.workspaceMembership?.user

  const userName = `${user?.name}${
    userData?.me.identifier === user?.identifier ? ' (You)' : ''
  }`

  return (
    <Truncate>
      <TooltipWithTruncatedContent
        placement="top"
        spacing="10px"
        content={userName}
        disabled={!isNameTruncated}>
        <Truncate ref={nameRef}>{userName}</Truncate>
        {!member.commentsEnabled && (
          <Breakpoint on="xs">
            <NoCommentIcon
              as={NoCommentSvg}
              aria-label="Member cannot comment on Artboards."
            />
          </Breakpoint>
        )}
      </TooltipWithTruncatedContent>
      <TooltipWithTruncatedContent
        placement="top"
        spacing="10px"
        content={member.email}
        disabled={!isEmailTruncated}>
        <UserEmail ref={emailRef}>{member.email}</UserEmail>
      </TooltipWithTruncatedContent>
    </Truncate>
  )
}

export const ShareMember = ({ member, share }: ShareMemberProps) => {
  const { ref, inView } = useInView({
    threshold: 0.25,
    // We are forcing this to true because cypress was having trouble finding
    // the elements even though they were on the screen. This won't cause any difference
    // in the UI itself.
    initialInView: true,
  })

  const {
    deleteMember,
    deleteMemberLoading,
    deleteMemberConfirmDialogOpen,
    getDeleteMemberConfirmDialogProps,
  } = useDeleteMember({
    share,
    member,
  })

  const {
    updateMemberLoading,
    updateMemberConfirmDialogOpen,
    getUpdateMemberConfirmDialogProps,
    getUpdateMemberDropdownProps,
    updateMemberLevel,
  } = useUpdateMember({ share, member })

  const isMemberTheCurrentUser = useIsMemberTheCurrentUser(member)
  // For Draft documents only, the owner/creator can't edit/remove themself
  // from the member's list.
  // Since for draft documents, the membership settings tab is not accessible
  // by invited members, the current user is always the owner and `isMemberTheCurrentUser`
  // means that the member is the owner of the draft document".
  const canEditLevelAccess = !share.isDraft || !isMemberTheCurrentUser

  const user = member.workspaceMembership?.user

  return (
    <>
      <Wrapper ref={ref} role="membership-row">
        <Flex flex="none">
          <Avatar
            src={user?.avatar?.small ?? undefined}
            name={user?.name ?? ''}
            size={32}
          />
        </Flex>
        <Flex
          mr={3}
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          overflowX="hidden"
          // This will take all the remaining vertical space
          // avoiding the scrollbar to appear, as the line height
          // of the pill is bigger than the current height of the email
          alignSelf="stretch">
          {user ? (
            <UserInfo member={member} />
          ) : (
            <NonUserInfo member={member} />
          )}
          <Breakpoint on="xs">
            {!deleteMemberLoading && badgeForMemberRole(member)}
          </Breakpoint>
        </Flex>
        <Flex ml="auto">
          {(updateMemberLoading || deleteMemberLoading) && (
            <Spinner.Flex mr={2} primary />
          )}
          {!canEditLevelAccess ? (
            <DropdownTitle disabled>Edit</DropdownTitle>
          ) : (
            <AccessLevelSelect
              {...getUpdateMemberDropdownProps()}
              deleteMember={deleteMember}
              inviteStatus={member?.inviteStatus}
              disabled={deleteMemberLoading || updateMemberLoading}
              isInView={inView}
            />
          )}
        </Flex>
      </Wrapper>
      {updateMemberConfirmDialogOpen && (
        <Portal>
          <ConfirmationDialog
            {...getUpdateMemberConfirmDialogProps()}
            title={`Change your permissions to ${capitalize(
              updateMemberLevel
            )}?`}>
            You will no longer be able to edit this document. If you need Edit
            permissions again later, ask a Workspace Admin.
          </ConfirmationDialog>
        </Portal>
      )}
      {deleteMemberConfirmDialogOpen && (
        <Portal>
          <ConfirmationDialog
            {...getDeleteMemberConfirmDialogProps()}
            title={'Remove yourself from this document?'}>
            If you need to add yourself again later, ask a Workspace Admin.
          </ConfirmationDialog>
        </Portal>
      )}
    </>
  )
}

/** Is the member the same as the current user => "You"? */
function useIsMemberTheCurrentUser(member: ShareMembershipInfoFragment) {
  const { data } = useUserProfile(true)

  return data?.me.identifier === member.workspaceMembership?.user?.identifier
}
